import { useEffect, useState } from 'react'
import {
  Card, Col, Empty, Row, Select, Space, Statistic, Switch, Tabs, Tag, Typography,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import {
  spareProcurementApi,
  type DirectChargeRow,
  type EnrichedLinkRow,
  type LinkStatus,
  type PendingLiabilityRow,
  type PipelineSummary,
} from '@/features/maintenance/spareProcurementApi'
import { departmentsApi } from '@/features/masters/departments/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ProcurementStatusBadge from './components/ProcurementStatusBadge'

const { Text } = Typography

const STAGE_OPTIONS: LinkStatus[] = [
  'AWAITING_APPROVAL', 'AWAITING_PO', 'AWAITING_GRN',
  'AWAITING_INVOICE', 'COMPLETE', 'CANCELLED',
]

/**
 * Phase 3.2-B — Spare Procurement Pipeline Dashboard.
 *
 * Single page with three lenses:
 *   • Pipeline       — every WO → PR → PO → GRN → Invoice link, enriched
 *   • Pending Payable — outstanding maintenance invoices
 *   • Direct Charge   — emergency spares that bypass stock
 *
 * Every number is fetched from the backend — no client-side math.
 */
export default function SpareProcurementDashboardPage() {
  const { plantId } = usePlantId()
  const [stage, setStage] = useState<LinkStatus | undefined>()
  const [departmentId, setDepartmentId] = useState<string | undefined>()
  const [directChargeOnly, setDirectChargeOnly] = useState(false)

  const { data: depts } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
    staleTime: 5 * 60_000,
  })
  const deptOptions = (depts?.items ?? []).map((d) => ({
    label: d.name, value: d.id,
  }))

  const summary = useQuery<PipelineSummary>({
    queryKey: ['sp-pipeline-summary', plantId],
    enabled: !!plantId,
    queryFn: () => spareProcurementApi.pipelineSummary(plantId),
  })

  const links = useQuery<EnrichedLinkRow[]>({
    queryKey: ['sp-links', plantId, stage, departmentId, directChargeOnly],
    enabled: !!plantId,
    queryFn: () => spareProcurementApi.listLinks({
      plant_id: plantId,
      status: stage,
      department_id: departmentId,
      direct_charge_only: directChargeOnly,
      limit: 1000,
    }),
  })

  const pending = useQuery<PendingLiabilityRow[]>({
    queryKey: ['sp-pending', plantId, departmentId],
    enabled: !!plantId,
    queryFn: () => spareProcurementApi.pendingLiability({
      plant_id: plantId, department_id: departmentId, limit: 1000,
    }),
  })

  const directCharge = useQuery<DirectChargeRow[]>({
    queryKey: ['sp-direct-charge', plantId],
    enabled: !!plantId,
    queryFn: () => spareProcurementApi.directCharge({
      plant_id: plantId, limit: 1000,
    }),
  })

  // Keep filters in sync with the plant selector
  useEffect(() => {
    if (!plantId) return
    summary.refetch()
    links.refetch()
    pending.refetch()
    directCharge.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId])

  const linkColumns = [
    {
      title: 'Work Order', dataIndex: 'work_order_no', key: 'wo', width: 130,
      render: (v: string) => (
        <Link to={`/maintenance/work-orders`}>{v}</Link>
      ),
    },
    { title: 'Machine', dataIndex: 'machine_code', key: 'm', width: 110 },
    { title: 'Department', dataIndex: 'department_name', key: 'd', width: 140 },
    { title: 'Spare', dataIndex: 'spare_code', key: 's', width: 120 },
    {
      title: 'Req Qty', dataIndex: 'required_qty', key: 'rq', width: 90,
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Stock', dataIndex: 'current_stock', key: 'cs', width: 90,
      render: (v: number | null) => v == null ? '—' : v.toFixed(2),
    },
    { title: 'PR', dataIndex: 'pr_no', key: 'pr', width: 130 },
    { title: 'PO', dataIndex: 'po_no', key: 'po', width: 130 },
    { title: 'Vendor', dataIndex: 'supplier_name', key: 'sup', ellipsis: true },
    {
      title: 'Stage', dataIndex: 'status', key: 'st', width: 160,
      render: (v: LinkStatus, r: EnrichedLinkRow) => (
        <Space size={4} wrap>
          <ProcurementStatusBadge status={v} />
          {r.direct_charge && <Tag color="purple">DIRECT</Tag>}
        </Space>
      ),
    },
    {
      title: 'Invoice', dataIndex: 'invoice_no', key: 'inv', width: 130,
      render: (v: string | null, r: EnrichedLinkRow) => v
        ? <Space size={4} wrap><Text>{v}</Text>
            {r.match_status && <ProcurementStatusBadge status={r.match_status} />}
          </Space>
        : '—',
    },
    {
      title: 'Payable', dataIndex: 'invoice_total_payable', key: 'pay', width: 130,
      render: (v: number | null) => v == null ? '—' : `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Last Updated', dataIndex: 'updated_at', key: 'lu', width: 130,
      render: (v: string | null, r: EnrichedLinkRow) => {
        const ts = v ?? r.created_at
        return ts ? dayjs(ts).format('DD MMM HH:mm') : '—'
      },
    },
    {
      title: 'Action', key: 'a', width: 150,
      render: (_: unknown, r: EnrichedLinkRow) => (
        <Link to={`/maintenance/spare-procurement/wo/${r.work_order_id}`}>
          View status
        </Link>
      ),
    },
  ]

  const pendingColumns = [
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'v' },
    { title: 'PO', dataIndex: 'po_no', key: 'po', width: 130 },
    { title: 'Work Order', dataIndex: 'work_order_no', key: 'wo', width: 130 },
    { title: 'Invoice', dataIndex: 'invoice_no', key: 'inv', width: 140 },
    {
      title: 'Invoice Date', dataIndex: 'invoice_date', key: 'id', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Invoice ₹', dataIndex: 'invoice_amount', key: 'ia', width: 130,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Paid ₹', dataIndex: 'paid', key: 'paid', width: 130,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Balance ₹', dataIndex: 'balance_payable', key: 'bp', width: 130,
      render: (v: number) => (
        <Text strong style={{ color: v > 0 ? '#cf1322' : '#52c41a' }}>
          ₹ {v.toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'match_status', key: 'ms', width: 150,
      render: (v: string) => <ProcurementStatusBadge status={v} />,
    },
    {
      title: 'Due Date', dataIndex: 'due_date', key: 'dd', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
  ]

  const directColumns = [
    { title: 'Work Order', dataIndex: 'work_order_no', key: 'wo', width: 130 },
    { title: 'Spare', dataIndex: 'spare_code', key: 's', width: 130 },
    { title: 'Spare Name', dataIndex: 'spare_name', key: 'sn', ellipsis: true },
    { title: 'Vendor', dataIndex: 'supplier_name', key: 'v', ellipsis: true },
    { title: 'PO', dataIndex: 'po_no', key: 'po', width: 130 },
    { title: 'GRN', dataIndex: 'grn_no', key: 'g', width: 130 },
    { title: 'Invoice', dataIndex: 'invoice_no', key: 'inv', width: 130 },
    {
      title: 'Qty', dataIndex: 'quantity_accepted', key: 'q', width: 90,
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Landed ₹/unit', dataIndex: 'landed_rate', key: 'lr', width: 130,
      render: (v: number) => `₹ ${v.toFixed(4)}`,
    },
    {
      title: 'Total Cost', dataIndex: 'total_cost', key: 'tc', width: 130,
      render: (v: number) => (
        <Text strong>₹ {v.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Posted to WO', dataIndex: 'work_order_id', key: 'p', width: 130,
      render: () => <Tag color="purple">DIRECT</Tag>,
    },
  ]

  const totalPayable = (pending.data ?? []).reduce(
    (a, r) => a + (r.balance_payable ?? 0), 0,
  )

  return (
    <div>
      <PageHeader
        title="Spare Procurement Pipeline"
        subtitle="Every spare from work-order request to vendor payment — backend-calculated, audit-tracked."
      />

      {/* Cards (top row) */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Awaiting approval"
              value={summary.data?.AWAITING_APPROVAL ?? 0}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Awaiting PO"
              value={summary.data?.AWAITING_PO ?? 0}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Awaiting GRN"
              value={summary.data?.AWAITING_GRN ?? 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Awaiting invoice"
              value={summary.data?.AWAITING_INVOICE ?? 0}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Complete"
              value={summary.data?.COMPLETE ?? 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Pending payable (₹)"
              value={totalPayable}
              prefix="₹"
              precision={2}
              valueStyle={{ color: totalPayable > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="Pipeline stage"
            style={{ width: 200 }}
            options={STAGE_OPTIONS.map((s) => ({ label: s, value: s }))}
            value={stage}
            onChange={setStage}
          />
          <Select
            allowClear
            placeholder="Department"
            style={{ width: 200 }}
            options={deptOptions}
            value={departmentId}
            onChange={setDepartmentId}
          />
          <Switch
            checkedChildren="Direct-charge only"
            unCheckedChildren="All"
            checked={directChargeOnly}
            onChange={setDirectChargeOnly}
          />
        </Space>
      </Card>

      <Tabs
        items={[
          {
            key: 'pipeline',
            label: `Pipeline (${links.data?.length ?? 0})`,
            children: links.isLoading ? (
              <Card><Text type="secondary">Loading…</Text></Card>
            ) : (links.data?.length ?? 0) === 0 ? (
              <Empty description="No procurement links yet" />
            ) : (
              <DataTable
                rowKey="link_id"
                columns={linkColumns}
                dataSource={links.data}
                loading={links.isLoading}
              />
            ),
          },
          {
            key: 'pending',
            label: `Pending Payable (${pending.data?.length ?? 0})`,
            children: pending.isLoading ? (
              <Card><Text type="secondary">Loading…</Text></Card>
            ) : (pending.data?.length ?? 0) === 0 ? (
              <Empty description="No outstanding maintenance invoices" />
            ) : (
              <DataTable
                rowKey="pve_id"
                columns={pendingColumns}
                dataSource={pending.data}
                loading={pending.isLoading}
              />
            ),
          },
          {
            key: 'direct',
            label: `Direct Charge (${directCharge.data?.length ?? 0})`,
            children: directCharge.isLoading ? (
              <Card><Text type="secondary">Loading…</Text></Card>
            ) : (directCharge.data?.length ?? 0) === 0 ? (
              <Empty description="No direct-charge spares received yet" />
            ) : (
              <DataTable
                rowKey="grn_line_id"
                columns={directColumns}
                dataSource={directCharge.data}
                loading={directCharge.isLoading}
              />
            ),
          },
        ]}
      />
    </div>
  )
}
